export const COUNTRIES = ['uz', 'kz', 'es', 'cl'] as const;
export type Country = (typeof COUNTRIES)[number];

/** Localizes a country's region names (e.g. raw Uzbek-Latin oblast names → Russian). */
export interface RegionCatalog {
  localize(name: string | null | undefined, locale: string): string | null;
}

/** Per-locale magnitude suffixes for compact budget formatting ("млрд",
 *  "mlrd", …), largest-magnitude first — paired positionally with the fixed
 *  [1e12, 1e9, 1e6, 1e3] threshold ladder in `compactMagnitude`. Keyed by the
 *  locale discriminator it selects on ('ru' | 'uz' today). */
export interface CurrencyConfig {
  readonly code: string;
  readonly symbol: string;
  readonly compactUnits: Record<string, readonly string[]>;
  /**
   * OPTIONAL — the singular of each {@link compactUnits} suffix, used when the
   * rounded figure is exactly 1 («$ 1 millón», not «$ 1 millones»). Chile, whose
   * units are words. Absent = the one form for every figure (UZ, KZ, ES — whose
   * «M» / «млн» do not inflect).
   */
  readonly compactUnitsOne?: Record<string, readonly string[]>;
  /** The currency WORD shown next to amounts, per UI locale (ru → «сум»/«тенге»,
   *  uz → «so'm»). The UI historically hardcoded «сум»/«so'm» inline; it now
   *  interpolates this via the i18n `{{currency}}` token so a KZ (KZT) build reads
   *  «тенге», a UZ (UZS) build «сум»/«so'm», from one source. Keyed by the locale
   *  discriminator, like `compactUnits`. */
  readonly words: Record<string, string>;
  /**
   * OPTIONAL — the BCP-47 locale whose number symbols budgets are written with
   * (`'es-ES'` → «1.234,56»). When set, `formatBudget` groups with that locale's
   * separators and keeps the cents of a fractional amount, via `Intl`.
   *
   * Absent on UZ and KZ, which keep the legacy space-grouped, whole-unit
   * formatter byte for byte (`Intl` is unsafe there: Chrome ships no `uz` CLDR
   * data). Spain stores euros WITH cents and Chrome does ship `es`, so `Intl` is
   * the right tool there.
   */
  readonly numberLocale?: string;
}

/** Site identity — was hardcoded as `SITE_NAME`/`SITE_URL` literals in
 *  `seo.tsx` and `prerender.ts`, plus the support address duplicated across
 *  notification senders (`support@uztender.com`). */
export interface BrandConfig {
  readonly name: string;
  readonly siteUrl: string;
  readonly supportEmail: string;
  /** The human support contact on Telegram that the site PUBLISHES next to
   *  `supportEmail` — the requisites card, the «Помощь и поддержка» modal and
   *  /about. `null` = no Telegram contact is shown at all.
   *
   *  ⚠️ NOT the same switch as `messaging.telegram`. That one says whether the
   *  deployment runs the Telegram BOT (alerts, login widget); this one says
   *  whether a person can be written to on Telegram — owner's call 2026-10-13:
   *  «контакты на KZ — Telegram и почта», no phone. */
  readonly supportTelegram: { readonly handle: string; readonly url: string } | null;
  /** Whether the operator's PHONE line (and the WhatsApp link, which is the same
   *  number) is published on this deployment. UZ: yes. KZ: no — owner's call
   *  2026-10-13, the number is the owner's own Uzbek mobile. The legal
   *  requisites (name, address, ИНН, bank) are NOT affected by this flag. */
  readonly publishPhone: boolean;
}

/** The deployment's country NAME, in the grammatical cases the copy needs. The
 *  UI hardcoded «Узбекистан»/«Узбекистана»/«Узбекистане» inline; it now
 *  interpolates these via the i18n tokens `{{country}}` (nominative),
 *  `{{countryGen}}` (genitive) and `{{countryPrep}}` (prepositional/locative),
 *  so a KZ build reads «Казахстан/Казахстана/Казахстане». Keyed by UI locale
 *  like `currency.words` / `compactUnits`. */
export interface CountryNameForms {
  /** nominative — «Казахстан» (e.g. «Республики Казахстан») */
  readonly nom: string;
  /** genitive — «Казахстана» (e.g. «госзакупки Казахстана») */
  readonly gen: string;
  /** prepositional/locative — «Казахстане» (e.g. «в Казахстане») */
  readonly prep: string;
}

/** First-party analytics identity that differs per deployment. `metaPixelId` is
 *  the Meta (Facebook) Pixel id — it was a hardcoded literal in `index.html`
 *  (UZ `1194754032777047`) and the dead `VITE_META_PIXEL_ID` env var. It now
 *  lives here so the Vite `transformIndexHtml` plugin bakes the right id per
 *  country with no env drift. The SERVER CAPI token is NOT here — it is a secret
 *  read from the per-host `.env` (`META_CAPI_ACCESS_TOKEN`), never shipped in the
 *  browser bundle. Public: the web build reads the pixel id at bundle time. */
export interface AnalyticsConfig {
  /** `''` = no Meta Pixel on this deployment yet (Spain, until the owner creates
   *  one): the web must not call `fbq('init')` with an empty id. */
  readonly metaPixelId: string;
  /** GA4 Measurement id (`G-…`). Baked into index.html per country by the Vite
   *  build, like `metaPixelId`. **Empty string disables GA4 for the deployment**
   *  (the gtag config + gtag.js load are skipped) — KZ ships `''` so KZ page
   *  views do NOT leak into UZ's GA4 property. UZ keeps its `G-06TE1QP5JR`. */
  readonly ga4MeasurementId: string;
  /**
   * OPTIONAL — `paywall_shown` counts «one person hit the wall on one lot» (Spain,
   * owner 23.10). It fires ONCE per (person, lot, page view), when the first
   * locked block scrolls into view or a padlock is tapped, and carries every
   * locked block of that lot in `meta.sections`. Absent (UZ, KZ) = one event per
   * locked block on mount, as before — the economics there are built on it.
   */
  readonly paywallOncePerLot?: boolean;
  /**
   * OPTIONAL — before the visitor answers the cookie banner (and after «Rechazar»)
   * PostHog runs COOKIELESS: nothing is stored on the device (`persistence: 'memory'`),
   * no replay, no autocapture, no person profile, only `$pageview` and a short list of
   * funnel events (`COOKIELESS_EVENTS` in `lib/posthog.ts`). «Aceptar» switches the same
   * instance to the full mode. Only meaningful with `features.cookieConsent`. The Meta
   * Pixel and the first-party beacon stay behind their grants. Absent (UZ, KZ) = no
   * PostHog before consent, as before.
   */
  readonly cookielessBeforeConsent?: boolean;
  /**
   * OPTIONAL — the share of CONSENTED sessions PostHog records end to end (`sampleRate`
   * of `session_recording`). Spain runs at `1` while its traffic is a few dozen visits a
   * day and the owner wants to watch every one (23.10); the sessions worth keeping are
   * also opted back in at full rate by `phStartRecording()`. Absent (UZ, KZ) = `0.1`, as
   * before — recording every bounce there buys nothing.
   */
  readonly replaySampleRate?: number;
}

/**
 * Editorial rules that differ per deployment. Public: the web and the API read it. Every
 * field is OPTIONAL and absent means «as before» (UZ, KZ).
 */
export interface ContentConfig {
  /**
   * A lot whose title is Catalan, Valencian, Galician or Basque (Spain) is shown with a
   * CASTILIAN title as the main one and the original underneath — «Título original: …» —
   * on the lot, the catalogue cards and the alert e-mails (owner 23.10). The Castilian
   * title is the AI translation that already sits in the `titleUz`/`titleRu` slots; this
   * flag adds the original line, makes the alert e-mails use the translation, and lets the
   * detector read the lot's description when its title says too little
   * (`needsCastilianTitle`). Lots in Castilian are never touched. Absent (UZ, KZ) = as before.
   */
  readonly castilianTitles?: boolean;
  /**
   * A live lot whose title is hard to read — 110+ characters of «objeto del contrato», a
   * boilerplate opening («El contrato tiene por objeto…»), a series or expediente code in
   * front («Exc344 —», «Lote 3 -») or a long ALL-CAPS line — is shown with a SHORT Castilian
   * title that says what is bought (owner 23.10). The rewrite goes into the same
   * `titleUz`/`titleRu` slots as the Castilian translation, so the «Título original: …» line
   * of {@link castilianTitles} prints the platform's own title underneath; one model call
   * covers a lot that needs both (`needsReadableTitle`, `localizeTitle`). Absent (UZ, KZ) =
   * as before.
   */
  readonly readableTitles?: boolean;
}

/** Browser-safe company-identifier descriptor for the web ИНН/БИН form (label +
 *  expected digit count). The full `CompanyIdConfig` (with the checksum `validate`
 *  fn) stays server-only; the browser only needs the length gate + label, and the
 *  server does the real checksum on submit. UZ ИНН/СТИР = 9 digits, KZ БИН/ИИН = 12. */
export interface CompanyIdFormConfig {
  readonly label: string;
  /** Exact length of the id. Digits-only schemes (UZ 9, KZ 12) — or, with
   *  {@link alphanumeric}, the id's length in characters (ES NIF 9). */
  readonly digits: number;
  /**
   * OPTIONAL — the id mixes letters and digits (Spain's NIF/CIF/NIE: `B12345674`,
   * `X1234567L`). When set, the forms keep `[0-9A-Z]` (upper-cased) instead of
   * digits only, and a value is complete when it matches `pattern` (an anchored
   * regex SOURCE — a string, so the config stays plain data). The browser still
   * checks only the shape; the checksum is server-side (`companyId.validate`).
   * Absent = digits only, exactly `digits` long (UZ, KZ — unchanged).
   */
  readonly alphanumeric?: { readonly pattern: string };
  /**
   * OPTIONAL — the company step and the settings card SAVE a company name the
   * reader typed when the register has no match for it (or for their NIF), so
   * the owner can see who registered (Spain, owner 2026-10-21: «если базы нет —
   * просто вбивает NIF, сохраняем; вбивает название — просто сохраняем»):
   * `POST /auth/me/company/name` → `users.typed_company_name`. Absent = UZ/KZ
   * unchanged: a typed name only looks a company up, and the endpoint refuses.
   */
  readonly keepsTypedName?: boolean;
  /**
   * OPTIONAL — the id is a Chilean RUT (Chile): the forms keep its digits and the
   * check digit (`K` upper-cased), drop the dots, and write it in the canonical
   * `digits-DV` spelling the API validates and stores (`76086428-5`) — the dash is
   * part of the id, so it is put back however the reader typed it
   * (`canonicalRut`). `digits` is then the canonical length's ceiling (10).
   * Absent = UZ, KZ, ES unchanged.
   */
  readonly format?: 'rut';
  /**
   * OPTIONAL — which ids belong to NATURAL PERSONS, not companies (Chile: a RUT
   * whose body is under 50 000 000, `CL_LEGAL_ENTITY_MIN_BODY` — the line the
   * company index already draws). Such an id's page is headed «Perfil del
   * proveedor» instead of «Perfil de la empresa», and the guest teasers of a lot
   * («Posibles oferentes», «Concentración de adjudicatarios») print `label`
   * instead of the person's name. Absent = no such distinction (UZ, KZ, ES —
   * unchanged).
   */
  readonly naturalPerson?: {
    readonly isPerson: (id: string) => boolean;
    readonly label: string;
  };
}

/** Title/code → ИКПУ (product-classifier) adapter. The UZ implementation
 *  (`uz.classifier.ts`) is a keyword heuristic; a different country plugs in its
 *  own scheme's classifier. Both methods are resolve-only — an unmatched input
 *  yields `null`, never a wrong guess. Server-side only — deliberately NOT on
 *  `PublicCountryConfig`: classification runs at ingest, and its rule tables have
 *  no business shipping in the browser bundle. `resolve` prefers a normalised
 *  source-provided `code`, falling back to classifying `title`; `classify` infers
 *  purely from a title. */
export interface ClassifierAdapter {
  resolve(code: string | null, title: string): string | null;
  classify(title: string): string | null;
}

/** The country's company-identifier scheme (ИНН/СТИР in UZ, a different
 *  scheme+shape elsewhere). `validate` says nothing about whether the taxpayer
 *  exists — only whether the string is syntactically usable (shape, not an
 *  obvious placeholder). See `uz.ts` for the UZ rules and why there is no
 *  check-digit verification. */
export interface CompanyIdConfig {
  readonly label: string;
  validate(id: string): boolean;
  /**
   * OPTIONAL — which registry ids the company step may SUGGEST (`GET
   * /auth/me/company/suggest`), as an anchored regex SOURCE that reads the same
   * in JavaScript and in Postgres (`organizations.tin ~ …`). Absent = exactly
   * `companyIdForm.digits` digits (UZ, KZ — unchanged).
   *
   * Spain: legal entities (CIF) only. A natural person's NIF (DNI/NIE — an
   * autónomo) is personal data, so it is never offered by name or by number —
   * the line company pages already draw for indexing (`companyIndex.indexableId`).
   * Such a person still types their own NIF in full, which `validate` accepts.
   */
  readonly suggestPattern?: string;
  /**
   * OPTIONAL — how a typed company NAME is matched (`company-suggest.ts`).
   * Absent = the UZ/KZ rule: a `simple` vector with the apostrophe fold, Uzbek /
   * Russian / Kazakh legal forms dropped from the query, a Cyrillic query also
   * run in Latin. `'es'` = Spain: accents folded on both sides (`unaccent`) and
   * Spanish legal forms (SL, SA, SLU, SAU, S.L., S.A.U. …) dropped at index AND
   * query time, through its own index (`organizations_name_fts_es_idx`, migration
   * `20261021120000_organizations_name_fts_es`).
   */
  readonly nameSearch?: 'es';
  /**
   * OPTIONAL — what the company step says once a KNOWN id is saved
   * (`company-step.service.ts`). Absent = the UZ/KZ order: competitive wins,
   * else open lots in the registry sector (ОКЭД), else in the region, else
   * nothing. `'es'` = Spain, whose register has no sector or region: EVERY award
   * counted and split — licitaciones (competitive), contratos menores, other
   * awards without publicity — with the CPV division most of them sit in and how
   * many lots are open there now; a NIF with no award says only that it was saved.
   */
  readonly payoff?: 'es';
}

/**
 * The bar a company page must clear to be INDEXABLE when it is not grandfathered
 * (`services/api/src/modules/catalog/company-index-rule.ts`): at least
 * `minCompetitiveAwards` competitive awards (not a direct contract — see
 * `competitive-awards.ts`) inside the last `windowMonths` months, and — where
 * `requireActiveStatus` — `organizations.status_group = 'ACTIVE'`.
 *
 * UZ (owner, 2026-10-10): 2 in 24 months, ACTIVE in the soliq registry.
 * KZ (owner, 2026-10-15): 5 in 36 months — «страницы для большинства компаний, у
 * кого 5 и больше выигранных тендеров» — and no status test, because no KZ
 * register says ACTIVE: `gbd_ul` only ever sets LIQUIDATED, so `status_group` is
 * empty on every Kazakh organisation and an ACTIVE requirement would index nobody.
 *
 * Server-side only, like `sourceKeys`.
 */
export interface CompanyIndexConfig {
  readonly minCompetitiveAwards: number;
  readonly windowMonths: number;
  readonly requireActiveStatus: boolean;
  /**
   * OPTIONAL — an id-level gate applied BEFORE every other arm (grandfather list
   * included): a company page whose registry id fails it is never indexable,
   * whatever its awards. Spain: legal entities only — a CIF starts with an
   * entity-type letter; a DNI / NIE / K-L-M NIF is a natural person (an
   * autónomo), whose page must never be indexed (GDPR). Receives the trimmed id.
   * Absent = no id gate (UZ, KZ — unchanged).
   */
  readonly indexableId?: (id: string) => boolean;
}

/**
 * Where an award price stops being credible, in the deployment's OWN currency
 * (so'm on UZ, tenge on KZ — the `*_uzs` columns hold local currency, the name
 * is historical). Read by the API's award guard
 * (`services/api/src/modules/catalog/award-sanity.ts` — `sanitizeAwardValue`,
 * `credibleAwardPrice()`: company totals, buyer/supplier pages, `/prices`,
 * market volume, the channel, the outcome copier) and by the data-plausibility
 * sweep (`plausibility-sweep.repository.ts`), which counts `tender_results` rows
 * with `winning_price_uzs < floor` or `>= ceiling`.
 *
 * UZ: 1,000 so'm / 10 трлн so'm — the literals the API carried before it read
 * them from here (specs pin them). KZ carries its own pair, chosen from the
 * live tenge price distribution (see `kz.ts`).
 *
 * Server-side only, like `sourceKeys`.
 */
export interface AwardPlausibilityConfig {
  /** Below this a winning price is not credible (strictly less-than). */
  readonly floor: number;
  /** At or above this a winning price is not credible. */
  readonly ceiling: number;
}

/**
 * The deployment's Telegram presence.
 *
 * UZ runs @uztender_com_bot (alerts, the login widget, the assistant Нигора) and
 * the public channel @uztender_uz. KZ runs its own bot @kaztender_com_bot (the
 * assistant Айгерим) since 2026-10-14 and has NO channel — a channel line or
 * footer must render nothing there rather than advertise the Uzbek one.
 */
export interface MessagingConfig {
  /** Whether the deployment runs a Telegram BOT: alerts, the login widget, the
   *  «подключить Telegram» prompts and the onboarding Telegram step. */
  readonly telegram: boolean;
  /** The bot's public username, without the @ — the one BotFather issued for
   *  this deployment. The server copy (/help) and the go-live check read it;
   *  the web bakes the same value from `VITE_TELEGRAM_BOT_USERNAME`, and the
   *  prod build refuses a mismatch. `null` when `telegram` is false. */
  readonly telegramBot: string | null;
  /** The public Telegram CHANNEL the copy advertises («📢 крупные тендеры каждый
   *  день»), or `null` for none. Not the bot and not the support contact. */
  readonly telegramChannel: { readonly handle: string; readonly url: string } | null;
  /** Whether a saved search that is created before any Telegram chat is linked
   *  gets its FIRST matching tender by e-mail. UZ: no — its activation moment is
   *  the Telegram connect, and an e-mail there would dedup-suppress the later
   *  Telegram send. KZ: yes — e-mail was its only channel until the bot, and a
   *  KZ account that never opens Telegram must still get the first alert. */
  readonly emailFirstAlert: boolean;
  /**
   * OPTIONAL — alert e-mails go to an address nobody has confirmed (Spain,
   * owner 2026-10-21: no confirmation step; bounces, complaints and the
   * unsubscribe link still stop them). Read by the API for the lots digest,
   * the results alert and the instant first alert (`sendFirstNicheMatch`):
   * an unconfirmed address is mailable when it is real, both toggles are on,
   * Postmark never bounced, complained about or suppressed it
   * (`emailBouncedAt`) and the mailbox is not paused. The one-time
   * «первая подборка» confirm block still rides the first digest of an
   * unconfirmed address. Read by the web for what it promises next to the
   * reader's selection. Absent or `false` = UZ and KZ: a confirmed (or
   * paying) address, plus that one first digest.
   */
  readonly emailWithoutConfirmation?: boolean;
  /**
   * OPTIONAL — ONE first alert per saved search, and it fits the search (Spain,
   * negative QA 22.10 Н1). Two paths send the instant first lot: the create-time
   * send of `POST /me/saved-searches` and `POST /me/first-alert`, which the
   * onboarding sheet calls right after the save. The create-time one queried the
   * REQUEST's single fields, and an ИИ-подбор sends only plural ones (codes,
   * regions, words) next to its categories — so it mailed the newest lot of the
   * category anywhere in the country, and the first-alert call then mailed the
   * right one: two emails, the first one wrong, on 6 of 6 AI saves. Under this
   * switch the create-time send reads the SAVED row (its first code, region and
   * word — what `/me/first-alert` reads), and neither path sends a second first
   * lot under a search that already received one. Absent = UZ and KZ, as before.
   */
  readonly singleFirstAlert?: boolean;
  /** OPTIONAL — the owner's money cards (оплата, брошенная оплата, отказ
   *  продления, отмена подписки, возврат) go by E-MAIL to `OWNER_ALERT_EMAIL`,
   *  and to Telegram only through the deployment's OWN bot
   *  (`CRM_TELEGRAM_BOT_TOKEN` + `CRM_TELEGRAM_CHAT_ID`), instead of the shared
   *  bot's CRM topic — whose payment, churn, abandonment and registration posts
   *  are then silent. Spain (owner, 2026-10-21: «уведомления мне — самое важное:
   *  оплата и брошенные корзины»): MiLicita has no bot, and one made later must
   *  never be the Uzbek one. Absent = off: UZ and KZ keep their CRM topic
   *  exactly as it is. See `services/api/src/modules/crm/owner-alerts/`. */
  readonly ownerAlertsByEmail?: boolean;
}

/** A side section of the catalogue — see `features.sections`. */
export type CatalogSection = 'ifi' | 'supply';

/** A payment mark the pricing page may print next to a pay button. */
export type PaymentMark =
  | 'payme'
  | 'click'
  | 'octobank'
  | 'uzcard'
  | 'humo'
  | 'visa'
  | 'mastercard'
  | 'stripe';

/** The legal entity that ACCEPTS card payments when it is not the operator
 *  named in the requisites (Kazakhstan: cards are charged by a US company
 *  through Stripe). Printed in the offer, the privacy policy and next to the
 *  pay button. */
export interface CardPayeeConfig {
  readonly name: string;
  readonly address: string;
  readonly phone: string;
  /** The processor the payee takes cards through — «Stripe». */
  readonly processor: string;
}

/**
 * How this deployment takes money. Read by the API (which plans are offered)
 * and by the web (what the pricing page renders) from the SAME object, so the
 * page can never show a way to pay the server would refuse.
 *
 * UZ is `local`: the Payme card sheet (card on file), hosted Payme / Click /
 * Octobank single payments, the yearly tab and the bank invoice — exactly what
 * it had before this block existed. KZ is `stripe`: ONE button per plan that
 * leaves for Stripe Checkout in subscription mode; no card form of ours, no
 * gateway chooser, no single payment, no yearly tab, no invoice (owner,
 * 2026-10-13).
 */
export interface PaymentsConfig {
  readonly checkout: 'local' | 'stripe';
  /** Yearly plans: the «Год» tab on /plans and the `*-yearly` keys in the
   *  server's offered list. */
  readonly yearlyPlans: boolean;
  /** «Оплата по счёту» (bank transfer against a numbered invoice). */
  readonly bankInvoice: boolean;
  /** Whether the `ip-*` tier is in the server's offered list at all. UZ keeps
   *  it there for its existing subscribers (the page hides the card); KZ has no
   *  such tier. */
  readonly ipPlan: boolean;
  /** Marks printed by the pay button. Nothing outside this list may be named
   *  on the deployment's pricing page. */
  readonly marks: readonly PaymentMark[];
  /** Who accepts card payments, when it is not the operator itself. */
  readonly cardPayee: CardPayeeConfig | null;
  /**
   * OPTIONAL — Stripe Tax on the Stripe checkout. No deployment uses it today:
   * Spain was going to (2026-10-20) until the owner decided the same day that
   * MiLicita collects no IVA ({@link vatNotCharged}). Kept for a country that
   * will. Absent = no tax collected and no tax line anywhere. Requires
   * {@link checkoutCollects} with both fields on (the schema refuses it
   * otherwise). See {@link StripeTaxConfig}.
   */
  readonly stripeTax?: StripeTaxConfig;
  /**
   * OPTIONAL — what the Stripe Checkout page asks the buyer for besides the
   * card, for the INVOICE, independent of any tax (Spain, owner 2026-10-20).
   * See {@link CheckoutCollectsConfig}. Absent = nothing beyond what Stripe
   * asks by itself: UZ and KZ, exactly as before this field existed.
   */
  readonly checkoutCollects?: CheckoutCollectsConfig;
  /**
   * OPTIONAL — the plan prices carry no VAT and none is added at checkout.
   * Spain (owner, 2026-10-20): the seller, AI CRM Inc, is a US company not
   * established in the EU and not registered for IVA there, so a Spanish
   * company or autónomo self-assesses it (inversión del sujeto pasivo, art.
   * 84.Uno.2.º LIVA). The pricing page says «IVA no incluido» after every
   * amount and gives that reason once. Never together with {@link stripeTax}.
   * Absent = no VAT wording at all (UZ, KZ).
   */
  readonly vatNotCharged?: boolean;
  /**
   * OPTIONAL — the plan prices INCLUDE VAT at this rate (Chile: `0.19`, IVA 19 %;
   * owner, Liciora AGENT-RULES §6: «Pro 39 990 CLP/mes IVA incluido»). Display
   * only: the pricing page says «IVA incluido» next to every amount and breaks
   * the IVA out of the price (`vatIncludedBreakdown`); the amount charged is the
   * plan price itself, never a price plus tax. Not Stripe Tax — never together
   * with {@link stripeTax} or {@link vatNotCharged} (the schema refuses both).
   * Absent = no such wording (UZ, KZ, ES).
   */
  readonly vatIncludedRate?: number;
}

/**
 * What the Stripe Checkout page collects from the buyer so the invoice can
 * name him — NOT a tax switch: both work without Stripe Tax («tax ID
 * collection» needs no `automatic_tax`, docs.stripe.com/tax/checkout/tax-ids,
 * read 2026-10-20). Read by the API only (`StripePaymentProvider`).
 */
export interface CheckoutCollectsConfig {
  /** `tax_id_collection` — a company enters its NIF / VAT number and its legal
   *  name; Stripe saves both on the Customer (`customer_update.name: 'auto'`)
   *  and prints them on every invoice of the subscription. */
  readonly taxId: boolean;
  /** `billing_address_collection: 'required'` — the full billing address,
   *  saved on the Customer (`customer_update.address: 'auto'`) and printed on
   *  the invoices. */
  readonly billingAddress: boolean;
  /**
   * OPTIONAL — our card sheet (`StripeCardModal`) shows the collapsed, optional
   * invoice block («Datos para el comprobante (opcional)») under the card fields,
   * so the details reach the Stripe Customer BEFORE the first receipt (Chile).
   * Absent = the sheet asks for the card only and the details are asked after the
   * payment, on the receipt page and in Ajustes (Spain, owner 2026-10-23).
   */
  readonly inCardSheet?: boolean;
}

/**
 * VAT collected by Stripe Tax on a `checkout: 'stripe'` deployment.
 *
 * Read by three parties, so they cannot disagree: the API (the Checkout Session
 * turns on `automatic_tax` — the tax id and the address it needs come from
 * {@link CheckoutCollectsConfig} — and refuses a Stripe Price whose
 * `tax_behavior` differs), the setup script (it creates the Prices with this
 * `tax_behavior`) and the web (the pricing page says «+ IVA» or «IVA
 * incluido» next to every amount).
 */
export interface StripeTaxConfig {
  /**
   * Is the plan price before tax (`exclusive`: 19 € + 21 % IVA = 22,99 € for a
   * Spanish consumer, 19 € for a company under reverse charge) or the final
   * figure (`inclusive`: everyone pays 19 €, the IVA is carved out of it)?
   *
   * A Stripe Price's `tax_behavior` can never change once set, so switching
   * this means new Prices: edit it, then run the setup script with
   * `--reprice` (docs/how-to/stripe-es-go-live.md).
   */
  readonly taxBehavior: 'exclusive' | 'inclusive';
}

/**
 * Which award rows (`tender_results`) are NOT a competition — contracts a buyer
 * signed with no competitive procedure, which the supplier-concentration reads
 * leave out (see `services/api/src/modules/catalog/competitive-awards.ts`).
 *
 * The two countries mark such a row differently, so the rule has two arms:
 *
 * - **by source** — the whole feed is direct purchases. UZ: `uzex-direct`
 *   (`GetDirectPurchases`); its rows carry no method at all.
 * - **by method** — one feed carries every procurement method and the row says
 *   which (`tender_results.method`). KZ: the goszakup contract registry and the
 *   goszakup results protocols hold «Из одного источника …» next to «Запрос
 *   ценовых предложений», «Открытый конкурс», … A row is non-competitive when
 *   its method contains one of the markers (case-insensitive substring). What a
 *   row with NO method is decides `unknownMethodIsCompetitive`.
 *
 * Server-side only, like `sourceKeys`.
 */
export interface CompetitionConfig {
  readonly nonCompetitiveSources: readonly string[];
  /** Lower-case substrings of `tender_results.method`. Empty = no method is a direct contract by name. */
  readonly nonCompetitiveMethodMarkers: readonly string[];
  /**
   * Does a row with no `method` count as a competition?
   *
   * UZ `true`: no Uzbek row carries a method, the source alone decides.
   * KZ `false`: where goszakup names the method, «из одного источника» is about
   * half of the rows that carry both БИНs, so a row whose method was never
   * harvested is as likely a direct contract as not — counting it would put
   * direct contracts back into the concentration read.
   *
   * The method arm is off only when there are no markers AND this is `true`
   * (UZ); then every read is exactly the source-only filter.
   */
  readonly unknownMethodIsCompetitive: boolean;
}

/** Full config (may reference Node-only deps once the classifier seam lands). */
export interface CountryConfig {
  readonly code: Country;
  readonly locales: readonly [string, ...string[]];
  readonly defaultLocale: string;
  readonly regions: RegionCatalog;
  readonly currency: CurrencyConfig;
  readonly brand: BrandConfig;
  /** Country display name per UI locale, in the cases the copy needs. */
  readonly countryName: Record<string, CountryNameForms>;
  /** Web-form company-id descriptor (label + digit count) — public half of `companyId`. */
  readonly companyIdForm: CompanyIdFormConfig;
  /** Example phone number shown as the placeholder in the phone-capture input,
   *  in the country's own dialling format (UZ `+998 …`, KZ `+7 …`). Display only —
   *  the value the user types is validated server-side, not against this. */
  readonly phonePlaceholder: string;
  /**
   * OPTIONAL — the IANA zone the deployment's calendar runs on: which «day» a
   * daily limit or the LLM spend fuse counts, when a cron fires, which date a
   * deadline or a «данные на …» stamp prints. Spain: `'Europe/Madrid'` (CET/CEST,
   * DST-aware — PLACSP deadlines are Madrid wall clock, stored as UTC instants).
   * It is the CUSTOMERS' calendar only: owner-facing times (admin, CRM, the MRR
   * midnight snapshot) stay on `OWNER_TIME_ZONE`, Tashkent, everywhere (owner
   * decision 2026-10-20).
   *
   * Absent = each call site keeps what it did before this field existed:
   * `Asia/Tashkent` where the code hardcoded Tashkent (UTC+5, no DST — and the
   * same clock as Kazakhstan, one zone since 2024-03-01, so KZ never
   * distinguished the two), and UTC where it read a date in UTC (the web's
   * deadline dates). Resolve it with `countryTimeZone()` / `displayTimeZone()`
   * (`time-zone.ts`), never `cfg.timeZone ?? …` inline.
   */
  readonly timeZone?: string;
  readonly analytics: AnalyticsConfig;
  /** OPTIONAL editorial rules — see {@link ContentConfig}. Absent = as before (UZ, KZ). */
  readonly content?: ContentConfig;
  /** Messaging channels this deployment offers for saved-search alerts and the
   *  login/connect surfaces. Public (on `PublicCountryConfig`): the web bundle
   *  reads it to gate those surfaces, and the prod build uses it to decide
   *  whether `VITE_TELEGRAM_BOT_USERNAME` is required. See {@link MessagingConfig}. */
  readonly messaging: MessagingConfig;
  /** Optional analytics/UX surfaces this deployment offers. `priceIntel` gates the
   *  three surfaces built on a real product-code (ИКПУ) taxonomy + price history:
   *  the `/market` dashboard, the `/prices` reference, and the goods/works/services
   *  `kind` filter. UZ has all three (years of `BidHistory` + the ИКПУ classifier);
   *  KZ's classifier is a stub with no price history, so they render near-empty and
   *  are hidden (`priceIntel: false`) rather than shown blank. Public (on
   *  `PublicCountryConfig`): the web bundle reads it to gate nav rows, the catalog
   *  `kind` filter, and the prerender/sitemap route list. `teamPlans` gates the
   *  seat-bearing «Бизнес» tier: UZ offers it; KZ does not for now (the server
   *  drops the `business-*` plans from the offered list, and the web never
   *  renders their card). */
  readonly features: {
    readonly priceIntel: boolean;
    readonly teamPlans: boolean;
    /**
     * Whether corporate-scope tenders must carry a buyer contact to appear in
     * browse (the contact gate). `true` where corporate sources publish a
     * contact (UZ banks). `false` where the only corporate source is
     * structurally contact-less (KZ mitwork lists a БИН and nothing else) — then
     * corporate is exempt from the gate like `ifi`/`supply`, otherwise the whole
     * layer is hidden. See `catalog-filters.ts` CONTACT_GATE.
     */
    readonly corporateContactGate: boolean;
    /**
     * OPTIONAL — whether `gov`-scope tenders must carry a buyer contact to appear
     * in browse. Absent or `true`: they must (UZ, KZ, ES — unchanged). `false`
     * (Chile): `gov` joins the exempt scopes. Compra Ágil fichas publish no buyer
     * contact at all (the adapter writes `contact: {}`) and 59 of 400 live
     * licitaciones have none either (phone QA 26.10, p1), so the gate hid every
     * Compra Ágil lot from list, facets, stats, search, hubs and alerts. Applied
     * by `catalog-filters.ts` to CONTACT_GATE and its SQL twins.
     */
    readonly govContactGate?: boolean;
    /**
     * Whether the `/start` and `/ip` conversion landings are served. They are
     * hand-written sales pages, not catalogue views: Uzbek prices in so'm, Uzbek
     * portals, «Оплата через Payme», the operator's phone. A deployment with no
     * copy of its own (KZ) must not serve — or prerender, or sitemap — the Uzbek
     * one under its own domain, so there the two paths redirect to the
     * catalogue. Turn it on for a country once that country's landing content
     * exists (`apps/web/src/content/landings`).
     */
    readonly conversionLandings: boolean;
    /**
     * ИИ-онбординг: the person describes in their own words what they supply,
     * and the server proposes a saved search grounded in this deployment's own
     * reference tables (categories, niches, product codes, regions) — the
     * onboarding search step and the «ИИ заполнит поля» block in the
     * saved-search builder. `true` = on for EVERY account, no PostHog flag.
     *
     * KZ: on (owner, 2026-10-15 «ИИ-онбординг врубай на КЗ»). UZ: off — the
     * Uzbek site keeps the category picker exactly as it was; with this off the
     * `/api/ai-search/*` endpoints answer 404 and the web never renders the step.
     */
    readonly aiOnboarding: boolean;
    /**
     * Pro teasers on the lot page promise only what Pro will show (owner's
     * complaint on KZ, 2026-10-15: «на превью данные есть, я покупаю, а здесь
     * их нет»): the market-data teaser blurs only metrics the comparables have,
     * the supplier-share / similar-winners teasers draw no more rows than the
     * data, the «Закрыто в Pro» card lists documents / contacts only when the
     * lot has them. `false` keeps the pre-2026-10-15 teasers byte for byte —
     * Uzbekistan's pre-purchase screen changes only on its owner's word.
     */
    readonly honestTeasers: boolean;
    /**
     * Whether this deployment ingests a `supply`-scope source (UZ: cooperation.uz
     * supply requests, `sourceKeys` has `cooperation`). Public, because the web
     * cannot read `sourceKeys`: with it off the search-result scope chips drop
     * «Кооперация» — KZ ingests no such source, so the chip could only ever
     * read 0 (phone QA, 2026-10-16). Pinned to `sourceKeys` by the country tests.
     */
    readonly supplyScope: boolean;
    /**
     * OPTIONAL — which of the two side sections of the catalogue this deployment
     * serves at all: `ifi` (World Bank & co. projects, `/ifi`) and `supply`
     * (supply requests, `/supply`). Absent (UZ, KZ) = both, exactly as before:
     * the routes render, and each sidebar row hides itself only once its count
     * has come back 0.
     *
     * A list (MiLicita: `[]` — PLACSP is the only source, so Spain has neither):
     * a section NOT listed has no page (`/ifi`, `/supply` redirect to the
     * catalogue), no sidebar row, no search chip, no count request and no
     * prerender/sitemap entry. Statically, so the first paint, the prerendered
     * HTML and a crawler never see «Organismos internacionales» with the Uzbek
     * development banks under it. Resolve with `catalogSectionServed()`.
     */
    readonly sections?: readonly CatalogSection[];
    /**
     * EU cookie consent (GDPR + LSSI-CE art. 22.2, AEPD «Guía sobre el uso de
     * las cookies»). OPTIONAL — absent or `false` changes nothing: no banner,
     * and every tracker loads exactly as it always has (UZ, KZ).
     *
     * `true` (MiLicita, Spain): no Meta Pixel / `_fbp` / `_fbc`, no PostHog, no
     * GA4 and no first-party analytics beacon until the visitor grants the
     * category; a banner with «Aceptar» / «Rechazar» and a «Configurar» link;
     * a footer link that reopens it; the API sends no Meta CAPI event and
     * records no analytics event for a request without the matching grant. The
     * choice lives in the first-party `tenders_consent` cookie
     * (`packages/shared/src/consent`).
     */
    readonly cookieConsent?: boolean;
    /**
     * The source publishes a lot's documents, its buyer contact and its own
     * notice page OPENLY, so this deployment must not sell them. OPTIONAL —
     * absent or `false` keeps the Uzbek/Kazakh paywall exactly as it is: the
     * files, the заказчик contact and «Открыть оригинал» are Pro.
     *
     * `true` — built for MiLicita (PLACSP's open ATOM feed carries the pliego
     * links, the órgano's e-mail/phone and the expediente link on every
     * record; brief §2, §4 phase 4), but Spain sets it `false` (owner,
     * 2026-10-20: «источник и документы НЕ бесплатно, всё ± как на
     * узтендер»). When on, for every viewer, guests included:
     *  - the document list ships with its links (PLACSP files are direct
     *    links to the platform, not the download proxy);
     *  - the link to the original notice, the platform's name and the lot's
     *    number ship and render;
     *  - the buyer contact ships inline on the lot, with no read ceiling and
     *    no `contact_viewed` (that event measures Pro use, and this is not Pro);
     *  - the lot card links to the buyer's public profile without a padlock.
     * What stays Pro: the AI read of the pliegos, the market/buyer analytics
     * (concentration, «Вероятные участники», price range), competitor
     * tracking, Excel, unlimited alerts. The award outcome on a lot is its own
     * switch, {@link outcomeWall}. Resolve with `sourceDataIsPublic()`.
     */
    readonly publicSourceData?: boolean;
    /**
     * Does the lot page (and /results, the alerts, the prerender) wall a lot's
     * single award — winner, company id, final price — behind Pro? OPTIONAL —
     * absent keeps the rule that predates this field: Uzbekistan walls it
     * (owner, 2026-10-07), every other deployment shows it (Kazakhstan:
     * goszakup publishes it). Spain sets `true` explicitly (owner, 2026-10-20),
     * although PLACSP publishes the adjudicatario and the importe. An owner switch.
     * Resolve with `outcomeWallOn()`, never `features.outcomeWall` inline.
     */
    readonly outcomeWall?: boolean;
    /**
     * «Próximos vencimientos»: awarded contracts whose term ends soon, so the
     * buyer is likely to re-tender — the `/vencimientos` page, its sidebar row,
     * `GET /api/results/expiring` and the prerender/sitemap entry. OPTIONAL —
     * absent or `false` changes nothing: no row, no route (the API answers 404),
     * nothing prerendered. Built for Spain (owner, 2026-10-20), where PLACSP
     * publishes the award date and the contract duration on the same record, so
     * the end date is computed rather than guessed (`tender_results.contract_end_at`).
     * The previous winner and the amount stay behind {@link outcomeWall}.
     * Resolve with `expiringContractsOn()`.
     */
    readonly expiringContracts?: boolean;
    /**
     * The catalogue's «goods / works / services» tabs. OPTIONAL — absent keeps
     * the rule that predates this field: the tabs follow {@link priceIntel}
     * (UZ and KZ, both `true`). Spain sets `true` explicitly: `priceIntel` is
     * off there (no price history yet), but every CPV code in the feed fills
     * `procurement_kind`, so the tabs count real lots (99 % of the live
     * catalogue, 20.10). Resolve with `kindTabsOn()`.
     */
    readonly kindTabs?: boolean;
    /**
     * The catalogue's first screen sells the free e-mail alert, as the ads do
     * (Spain, review 21.10 «first hour», item 1): the spotlight card says «we
     * e-mail you every new lot, free», and its button works WITHOUT a category
     * picked — a guest goes to /register and lands on «¿Qué ofreces?» (the
     * onboarding search step), a signed-in reader gets that step at once.
     * OPTIONAL — absent or `false`: the card, its copy and the greyed-out
     * «Сохранить поиск» stay exactly as they are (UZ, KZ).
     */
    readonly alertLanding?: boolean;
    /**
     * Pro is pitched by what the source does NOT give for free (Spain, review
     * 21.10 «first hour», item 3 — PLACSP publishes the contact, the pliegos
     * and the notice openly): the /plans Pro card and the lot's Pro wall lead
     * with who wins, for how much and against whom, then unlimited alerts; the
     * buyer contact, the documents and the official notice close the list as
     * one line; the refund guarantee sits under the price. The Pro set and
     * every padlock are unchanged — only the order and the words. OPTIONAL —
     * absent or `false`: the card and the wall as they are (UZ, KZ).
     */
    readonly marketFirstPitch?: boolean;
    /**
     * The lot's locked market-data teaser shows ONE real number to everyone —
     * the comparables' median bidder count («Licitadores (mediana): N») — and
     * the budget card carries «¿Por cuánto se gana? Baja mediana en N lotes
     * parecidos: ▒ % 🔒», linking to the card (Spain, review 21.10 «first
     * hour», item 4: one true figure takes the bait look off the blur — the
     * honest-teaser pattern of main `ddb693e1`). Both only when at least
     * {@link FREE_MARKET_MIN_LOTS} comparables carry the figure. The locked
     * API payload gains exactly the bidder median and the discount's lot
     * COUNT — never a discount, a price or a range. OPTIONAL — absent or
     * `false`: the teaser blurs every value and sends none (UZ, KZ).
     */
    readonly freeBidderMedian?: boolean;
    /**
     * The ИИ-онбординг (`aiOnboarding`) asks the company in its OWN step (Spain,
     * owner 2026-10-22). Step 1 «¿Qué ofreces?» is the description alone — no
     * «o NIF» door under it. The company id is asked in the company step, and
     * once it is known and has awards that step OFFERS (one line + a button,
     * never silently) to add the CPV codes of those awards to the search just
     * saved — `GET /ai-search/company-codes`, the same won-codes grounding the
     * door used, with no model call and no quota spent. OPTIONAL — absent or
     * `false`: the AI step keeps its company-id door (KZ), the endpoint answers
     * 404, and the company step is what it always was (UZ, KZ).
     */
    readonly aiCompanyStep?: boolean;
    /**
     * The purchase comes before the onboarding (Spain, negative QA 22.10, A4).
     * A guest's pay button on /plans sends him to /register — a buyer is
     * usually a new reader — rather than /login, with the plan stashed, so the
     * account comes back to `/plans?plan=…`; with the card form on our site
     * (Stripe Elements) that return opens it once. The onboarding sheet never
     * opens BY ITSELF on /plans or /billing/return: it waits for the next page
     * (on UzTender 48 % of first purchases came within six minutes of
     * registering). A request by hand still opens it anywhere. OPTIONAL —
     * absent or `false`: the guest goes to /login and the sheet opens wherever
     * it did (UZ, KZ).
     */
    readonly purchaseFirst?: boolean;
    /**
     * The ИИ-онбординг can be gone back to (Spain, negative QA 22.10, C7/C10/C11).
     * A reader without a saved search finds «Configurar mis alertas con IA» on
     * /app/alerts, on the catalogue's first-ask card and on its empty result;
     * a click opens «¿Qué ofreces?» at once, past the three-showings gate (which
     * keeps governing the sheet that opens by itself). On the proposal,
     * «Cambiar» returns to the description, text kept, instead of saving the
     * search and opening the filter editor — so the second free attempt the
     * counter promises can actually be spent. OPTIONAL — absent or `false`: no
     * entry, and «Изменить» saves and opens the editor as before (UZ, KZ).
     */
    readonly aiOnboardingReentry?: boolean;
    /**
     * A catalogue page past the last one says so (Spain, negative QA 22.10,
     * A2.7): `?page=999` drew «No hay licitaciones que cumplan los filtros
     * actuales» under «504 licitaciones», as if the filters had found nothing,
     * and was `index, follow` as «página 999». Now: «No hay más resultados», how
     * many pages there are, a link to page 1, and `noindex`. OPTIONAL — absent or
     * `false`: the empty list as before (UZ, KZ).
     */
    readonly pastLastPage?: boolean;
    /**
     * /login says its own errors (Spain, negative QA 22.10, B5): the email and
     * password forms are `noValidate`, so an empty field or «foo@» is answered
     * in the page, in the reader's language — «Introduce tu email», the email
     * typo sentence, «Introduce la contraseña» — under the field, where the API's
     * refusals already appear, instead of the browser's bubble («Please fill out
     * this field.», in the browser's language, gone on the next tap). OPTIONAL —
     * absent or `false`: the browser's own validation, as before (UZ, KZ).
     */
    readonly loginOwnErrors?: boolean;
    /**
     * A saved search opens the catalogue with its WHOLE definition (Spain, CPV
     * check 22.10, defect 1): the «+N más» link, the sidebar row, «Ver la lista»
     * after saving and the instant first lot carry the search's criteria as one
     * `alert=` param — every code, every word, every region, the budget and the
     * vetoes — compiled by the same `compileCriteria` the alert sweep runs. The
     * scalar params they used before held the FIRST code, the FIRST word and the
     * category, which the catalogue intersects where the alert unions: an alert
     * of 10 lots opened a list of 0. OPTIONAL — absent or `false`: the scalar
     * links and the scalar first-lot query, byte for byte (UZ, KZ).
     */
    readonly alertCriteriaLink?: boolean;
    /**
     * A saved search that names CPV codes matches by its CODES (Spain, CPV
     * check 22.10, defect 2): its words no longer narrow it. The AI proposes
     * words beside the codes, and ANDing them cut the codes' own lots — code
     * 55100000 had 25 open lots and the alert sent 5, because «restauración»
     * never reads «restaurante». Words narrow only a search with no codes (a
     * text-only niche); with codes they stay stored, shown and explained. One
     * rule for the digest sweep, the backfill, the instant first lot, «Tu
     * selección» (`openNow`), the preview count and the `alert=` link — all go
     * through `compileCriteria`/`includeTextTerms`. Region, budget, kind and
     * vetoes still apply. OPTIONAL — absent or `false`: the words AND as
     * before (UZ, KZ).
     */
    readonly codesOverTerms?: boolean;
    /**
     * A code chip takes its name from the FULL code table (Spain, CPV check
     * 22.10, defect 5): the saved-search editor asks `GET /catalog/ikpu/names`
     * for the codes it shows. The browse list it read before keeps a deep code
     * only with 5+ lots, so 55 % of the codes live lots carry rendered as a bare
     * number (`03411000`). OPTIONAL — absent or `false`: names from the browse
     * list and the AI's own, as before (UZ, KZ).
     */
    readonly codeNamesFromTable?: boolean;
    /**
     * «~N al día» on an AI proposal counts lots by the BUYER's publication date
     * (Spain, CPV check 22.10, defect 7): lots matching the proposal published
     * in the last 14 full days, whatever their status now, ÷ 14 — the definition
     * the alerts page's young-search estimate (`estimateRatePerDay`) already
     * uses. The default counts OPEN lots WE INGESTED in the last 14 days, which
     * on a young catalogue is its whole bulk import: on MiLicita (catalogue 2.4
     * days old, 8 765 of 13 275 open lots imported on 19.10) that read 948/day
     * for the catalogue against 507 published; dividing by the catalogue's age
     * instead would have read 5 482. OPTIONAL — absent or `false`: the ingest
     * window, as before (UZ, KZ).
     */
    readonly ratePerDayByPublication?: boolean;
    /**
     * A guest's «Guardar tu búsqueda» keeps the search through registration
     * (Spain, persona test 22.10, D1). The sidebar's guest link was a bare link to
     * /register — it stashed nothing, so a guest who had filtered the catalogue
     * (CPV 45000000 + Andalucía + budget ≥ 100 000) registered, found 0 saved
     * searches and was asked «¿Qué ofreces?» from scratch. Now the link stashes
     * the catalogue's filters from the address bar (the filter-bar button also
     * carries the budget floor), the replay after sign-in creates the search, and
     * the onboarding sheet WAITS for that replay — so it opens on the next step,
     * the NIF, instead of asking for the niche it already has. OPTIONAL — absent
     * or `false`: the sidebar link and the sheet as before (UZ, KZ).
     */
    readonly guestSearchCarried?: boolean;
    /**
     * A SIGNED-IN reader's «Guardar búsqueda» starts from the catalogue's budget
     * band (Chile, phone QA 26.10): the popover's «Presupuesto desde» was always
     * blank, so a search saved from `?minBudget=50000000` stored
     * `minBudgetUzs: null` and its alert matched lots of any size — the first
     * match for «≥ 50 M» was a lot with no budget at all. Now the field is
     * prefilled with the floor (still editable) and the ceiling travels with
     * the search. OPTIONAL — absent or `false`: the field starts blank and no
     * ceiling is sent, as before (UZ, KZ, ES).
     */
    readonly saveSearchCarriesBudget?: boolean;
    /**
     * A purchase intent is scoped and expires (Spain, persona test 22.10, D2). Tapping
     * a lot's padlock wrote a checkout stash (`/plans?…&tender=…`) that sent EVERY
     * later sign-in back to /plans — so a guest who then pressed «Crear alerta
     * gratis» on the home page registered, landed on `/plans?tender=…` and got
     * neither the onboarding nor the alert. Now: only a stash whose pay control was
     * PRESSED (`intent: 'pay'`) and not older than 30 minutes steers a sign-in back
     * to /plans, and /register drops the stash when it was reached from anywhere but
     * the purchase (the plan page). The purchase-first path — pay on /plans →
     * /register → /plans with the card form open — is unchanged. OPTIONAL — absent
     * or `false`: any stash sends the sign-in to /plans, for two hours (UZ, KZ).
     */
    readonly purchaseIntentScoped?: boolean;
    /**
     * A saved search that names CPV codes matches by its CODES, not by its category
     * (Spain, persona test 22.10, D5). The compiled criteria unions the subject axis
     * — codes OR category — so «limpieza de oficinas…» (category «Limpieza, higiene
     * y jardinería» + CPV 90919200/90911200/90911000) matched the WHOLE category:
     * of 34 lots 11 carried a 9091* code, 11 were gardening (77311000/77312xxx), the
     * rest cosmetics, disinfection and asbestos. With this switch the category is
     * left out of the match whenever the search has at least one code (the codes
     * stay, and so do region, budget, kind and the vetoes); it stays STORED and
     * shown as a label. One rule in `compileCriteria`, which the alert sweep, the
     * backfill, the instant first lot, «Lotes abiertos ahora», the preview count
     * and the `alert=` list all run. A search without codes keeps its category.
     * OPTIONAL — absent or `false`: the category unions with the codes (UZ, KZ).
     */
    readonly codesOverCategory?: boolean;
    /**
     * «Omitir» on the onboarding sheet's company step is remembered on the ACCOUNT
     * (Spain, persona test 22.10, D6). The skip only closed the sheet for the visit:
     * the once-a-day gate lives in `localStorage` and a sheet opened on request does
     * not count against it, so «¿Nombre o NIF de tu empresa?» came back on the next
     * reload, on every device. Now the press writes `users.company_step_skipped_at`
     * (`POST /auth/me/company/skip`), `GET /auth/me` reports it as
     * `companyStepSkipped`, and the sheet, the catalogue strip and the dashboard
     * checklist stop asking for the company. The way back stays: «Configurar mis
     * alertas con IA» opens the sheet on request and still offers the NIF step, and
     * the settings card takes a NIF at any time. OPTIONAL — absent or `false`: the
     * endpoint answers 404, `/auth/me` carries no such field, and the skip only
     * closes the sheet (UZ, KZ).
     */
    readonly companySkipRemembered?: boolean;
    /**
     * A refused one-time code empties the six boxes and puts the caret in the first
     * (Spain, persona test 22.10, D7 — «QA B9 not closed»). The clearing was written
     * for `err.code === 'invalid_code'`, but the API throws `UnauthorizedException
     * ('invalid_code')`, a STRING exception, which the HTTP error filter wraps as
     * `{ code: 'http_error', message: 'invalid_code' }` — so the web never recognised
     * the refusal it had a handler for. After a wrong code on /login (and /register)
     * the boxes kept «000000», the focus fell to `body` and Backspace did nothing
     * until a box was tapped. With this switch the refusal is also recognised by its
     * message. OPTIONAL — absent or `false`: only the `code` spelling is, as before
     * (UZ, KZ).
     */
    readonly refusedCodeCleared?: boolean;
    /**
     * `/billing/return` says «no hemos recibido ningún pago» when there is none
     * (Spain, persona test 22.10, D8). The page is where every hosted or on-site
     * payment lands — `?order=…` — and it thanked ANY visitor: a reader who opened
     * `/billing/return` by hand, with nothing paid and nothing pending, read «¡Gracias
     * por tu pago! Hemos recibido el pago…». Now: without an `order` (or the card
     * hand-off `src=card`, or Stripe's `redirect_status`) in the address, or with an
     * `order` that is not one of the reader's own, it shows the neutral state — «Si
     * has pagado, llegará en unos minutos; si no, vuelve a Planes» — with a link to
     * /plans. A paid reader, a refused payment and a payment still settling keep their
     * pages. OPTIONAL — absent or `false`: the thank-you as before (UZ, KZ).
     */
    readonly neutralBillingReturn?: boolean;
    /**
     * The company step does not say «este NIF ya está vinculado a otra cuenta» (Spain,
     * persona test 22.10, D14). `POST /auth/me/company` always STORES a valid id (owner,
     * 2026-10-18: «может это новая компания») and then tries to link the matching
     * supplier row to the account; when another account already holds that row it
     * reported `supplierLinkConflict`, and the step printed «NIF guardado» and «Este
     * NIF ya está vinculado a otra cuenta» together — two answers to one press. On UZ
     * and KZ that line belongs to the «claim your company» product (an account owns a
     * supplier profile and its match alerts); MiLicita has no such product, and the
     * line would only confirm to a stranger that a NIF is registered on another
     * account. So Spain saves silently: the NIF is kept, the conflict is only logged.
     * OPTIONAL — absent or `false`: the conflict is reported on the payoff and printed,
     * beside the saved message, as before (UZ, KZ).
     */
    readonly companyLinkNoticeHidden?: boolean;
    /**
     * A keyword typed while narrowing filters are still on the page (`?q=reforma&region=ES11
     * &category=construction&ikpu=45213150`) keeps a line over the results — «Resultados para
     * «reforma» con los filtros activos» — with a one-tap «Buscar en todo el catálogo» that
     * drops those filters and keeps the word (Spain, owner 23.10). Without it the word is
     * searched inside the filters and the page says nothing: «reforma» found 0 lots and
     * «Obras» 1 on 21.10 for exactly that reason, in a catalogue that holds 271 and 2 578.
     * (The empty-result card already offers the way out at 0; the line covers 1, 2, 15.)
     * Absent (UZ, KZ) = no such line, as before.
     */
    readonly searchScopeLine?: boolean;
  };
  /** How this deployment takes money — see {@link PaymentsConfig}. Public: the
   *  pricing page and the API's offered-plan list read the same flags. */
  readonly payments: PaymentsConfig;
  /** `TenderSource.key` values this deployment ingests. Server-side only — the
   *  ingestion scheduler intersects its registered adapter keys against this
   *  list, so a source outside it is never enrolled even if its DB row is
   *  enabled. Deliberately NOT on `PublicCountryConfig`: which sources feed a
   *  deployment is not something the browser bundle needs or should ship. */
  readonly sourceKeys: readonly string[];
  /** Which award rows are direct contracts, not tenders — see
   *  {@link CompetitionConfig}. Server-side only. */
  readonly competition: CompetitionConfig;
  /** Company-identifier (ИНН/СТИР) validation. Server-side only — deliberately
   *  NOT on `PublicCountryConfig`: `validate` is a function, which cannot cross
   *  the server/browser-bundle boundary the public view exists for (and the
   *  browser has no business re-deriving this rule from a shared config either
   *  — see `services/api/src/modules/auth/validate-tin.ts`, the sole owner of
   *  server-side company-id checks). */
  readonly companyId: CompanyIdConfig;
  /** Product (ИКПУ) classifier. Server-side only — deliberately NOT on
   *  `PublicCountryConfig`: it is invoked at ingest to derive a lot's ИКПУ code,
   *  and its keyword tables must not enter the browser bundle. See
   *  `services/api/src/modules/ingestion/ikpu-classifier.ts` (which re-exports
   *  the UZ implementation for the ingestion pipeline). */
  readonly classifier: ClassifierAdapter;
  /** Indexability bar for company pages — see {@link CompanyIndexConfig}. Server-side only. */
  readonly companyIndex: CompanyIndexConfig;
  /** Credible award-price band, local currency — {@link AwardPlausibilityConfig}. Server-side. */
  readonly awardPlausibility: AwardPlausibilityConfig;
  /**
   * OPTIONAL — how `POST /auth/start` treats an address that already has an
   * account. See {@link AuthConfig}. Server-side only. Absent = the shared rule,
   * exactly as UZ and KZ have it.
   */
  readonly auth?: AuthConfig;
  /**
   * OPTIONAL — what the catalogue's HOME (the unfiltered browse, `/` and its pages)
   * leaves out. See {@link HomeFeedConfig}. Server-side only. Absent = the home lists
   * every source, exactly as UZ and KZ have it.
   */
  readonly home?: HomeFeedConfig;
  /**
   * OPTIONAL — what the deployment's email assistant (Лусия, Spain) may DO, not
   * only say. See {@link SupportConfig}. Server-side only. Absent = the
   * assistant answers and hands over, exactly as Нигора and Айгерим do on UZ and KZ.
   */
  readonly support?: SupportConfig;
}

/**
 * The catalogue's home feed — the plain, unfiltered browse (persona test 22.10, D12a).
 *
 * MiLicita's first twelve cards were all «Consulta preliminar del mercado»: feed 1403
 * was seeded on 22.10 with the newest timestamps, and an ad visitor opening the site
 * saw twelve notices that are not tenders (the órgano asks companies before it calls
 * one) and none for his trade. The sources listed here are left out of the plain browse
 * — its list, its total, the per-type tab counts and the header's «N abiertas ahora»
 * — and ONLY of it: any filter, a search, a region or category page, the lot's own page,
 * the sitemap and the alert sweep still carry them.
 */
export interface HomeFeedConfig {
  /** `tender_sources.key` values the plain browse omits. */
  readonly excludeSources: readonly string[];
}

/**
 * What the support-email assistant is allowed to do on this deployment.
 * Server-side only: the web never reads it.
 */
export interface SupportConfig {
  /**
   * The assistant sets up and adjusts the writer's saved search herself, from
   * the letter (Spain, owner 2026-10-23: «Люсия должна полноценно консультировать
   * и сразу проактивно настраивать поиск для тех, кто пишет по почте»). Turns on,
   * together and only here: the account facts in her context, the
   * `support_search_created` / `support_search_updated` actions on the account
   * whose email is the sender's, the narrower owner hand-over (money and law
   * only) and the one-line Russian post to the owner after each answer. Resolve
   * with `assistantManagesSearches()` (`services/api/src/common/country.ts`).
   * Absent or `false` = UZ and KZ, which keep their assistant exactly as it was.
   */
  readonly assistantManagesSearches?: boolean;
}

/**
 * Sign-in rules that differ per deployment. Server-side only: the web reacts to
 * the answer of `/auth/start` (`code_required` shows the code step it already
 * has) and needs no flag of its own.
 */
export interface AuthConfig {
  /**
   * An EXISTING account always proves its mailbox with an emailed one-time code
   * — never signed in on its address alone, verified or not, paid or not.
   *
   * The shared rule (UZ, KZ — `AuthService.startAuth`) signs an EMPTY soft
   * account (passwordless, unverified, never paid) straight in, so a guest
   * whose card was refused retries without a code step; anyone who types such
   * an address gets its session. Spain (2026-10-20, measured on the live site)
   * closes that: its transactional mail works (Postmark), so the code costs a
   * returning buyer one letter. A password account still answers
   * `password_required` (a proof too); a brand-new address is still signed in
   * on the spot. Resolve with `codeRequiredForExistingAccounts()`
   * (`services/api/src/common/country.ts`).
   */
  readonly codeRequiredForExisting?: boolean;
}

/** Web-safe view — the subset the browser bundle may import (no Node deps). */
export type PublicCountryConfig = Pick<
  CountryConfig,
  | 'code'
  | 'locales'
  | 'defaultLocale'
  | 'regions'
  | 'currency'
  | 'brand'
  | 'countryName'
  | 'companyIdForm'
  | 'phonePlaceholder'
  | 'timeZone'
  | 'analytics'
  | 'content'
  | 'messaging'
  | 'features'
  | 'payments'
>;
// Widen the Pick as public fields are added.

export function assertNever(x: never): never {
  throw new Error(`Unhandled country/variant: ${String(x)}`);
}
